import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { LeagueAdminRepository } from "@/modules/leagues/repositories/LeagueAdminRepository";
import type { LeagueRole } from "@/modules/leagues/types/league";
import type {
  BonusQuestion,
  Club,
  GeneralSettingsInput,
  HistoryEntry,
  LeagueParticipant,
  ManualAdjustment,
} from "@/modules/leagues/types/admin";
import { footballDataService } from "@/services/football/FootballDataService";

function toIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  return new Date().toISOString();
}

/**
 * Implementação REAL (Firestore) de LeagueAdminRepository — substitui
 * MockLeagueAdminRepository, que nunca persistia nada de verdade (bug raiz
 * de "Participantes aparece vazio" e "Importar Pontos não credita no
 * ranking" — essas telas liam/escreviam num Map em memória que resetava a
 * cada carregamento de página, nunca o Firestore).
 */
export class FirestoreLeagueAdminRepository implements LeagueAdminRepository {
  private groupRef(leagueId: string) {
    return doc(db, "groups", leagueId);
  }

  private async pushHistory(leagueId: string, entry: Omit<HistoryEntry, "id" | "createdAt">) {
    await addDoc(collection(db, "groups", leagueId, "history"), { ...entry, createdAt: serverTimestamp() });
  }

  async getClubs(competitionId: string): Promise<Club[]> {
    return footballDataService.getClubs(competitionId);
  }

  async updateDisabledClubs(leagueId: string, disabledClubIds: string[]): Promise<void> {
    await updateDoc(this.groupRef(leagueId), { disabledClubIds });
    await this.pushHistory(leagueId, {
      type: "rule_updated",
      descricao: `Clubes desativados atualizados (${disabledClubIds.length} clube${disabledClubIds.length === 1 ? "" : "s"} fora da pontuação).`,
      administrador: auth.currentUser?.displayName?.trim() || "Você",
    });
  }

  async updateGeneralSettings(leagueId: string, input: GeneralSettingsInput): Promise<void> {
    await updateDoc(this.groupRef(leagueId), {
      nome: input.nome,
      descricao: input.descricao,
      maxParticipantes: input.maxParticipantes,
      privada: input.privada,
      permiteDesafios: input.permiteDesafios,
      permitePerguntasBonus: input.permitePerguntasBonus,
      permiteImportacaoManual: input.permiteImportacaoManual,
      permiteEntradaAposInicio: input.permiteEntradaAposInicio,
    });
    await this.pushHistory(leagueId, {
      type: "rule_updated",
      descricao: "Configurações gerais da liga atualizadas.",
      administrador: auth.currentUser?.displayName?.trim() || "Você",
    });
  }

  async getHistory(leagueId: string): Promise<HistoryEntry[]> {
    const snap = await getDocs(query(collection(db, "groups", leagueId, "history"), orderBy("createdAt", "desc")));
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        type: data.type,
        descricao: data.descricao,
        administrador: data.administrador,
        createdAt: toIso(data.createdAt),
      };
    });
  }

  async getManualAdjustments(leagueId: string): Promise<ManualAdjustment[]> {
    const snap = await getDocs(query(collection(db, "groups", leagueId, "adjustments"), orderBy("createdAt", "desc")));
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        participantUid: data.participantUid,
        participantNome: data.participantNome,
        pontos: data.pontos,
        motivo: data.motivo,
        administrador: data.administrador,
        createdAt: toIso(data.createdAt),
      };
    });
  }

  async addManualAdjustment(leagueId: string, participantUid: string, pontos: number, motivo: string): Promise<void> {
    const participantSnap = await getDoc(doc(db, "groups", leagueId, "participants", participantUid));
    const participantNome = participantSnap.exists() ? (participantSnap.data().nome as string) : "Participante";
    const administrador = auth.currentUser?.displayName?.trim() || "Você";

    await addDoc(collection(db, "groups", leagueId, "adjustments"), {
      participantUid,
      participantNome,
      pontos,
      motivo,
      administrador,
      createdAt: serverTimestamp(),
    });

    // Isto é o que efetivamente credita no ranking — sem isto o ajuste
    // ficava só registrado no histórico, mas `pontos` do participante
    // (a fonte que Ranking/Estatísticas leem) nunca mudava de verdade.
    await updateDoc(doc(db, "groups", leagueId, "participants", participantUid), {
      pontos: increment(pontos),
    });

    await this.pushHistory(leagueId, {
      type: pontos >= 0 ? "bonus" : "penalty",
      descricao: `${pontos >= 0 ? "+" : ""}${pontos} pts para ${participantNome} — ${motivo}`,
      administrador,
    });
  }

  async getParticipants(leagueId: string): Promise<LeagueParticipant[]> {
    const snap = await getDocs(collection(db, "groups", leagueId, "participants"));
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        uid: data.uid,
        nome: data.nome,
        avatarUrl: data.avatarUrl ?? null,
        role: data.role as LeagueRole,
        pontos: data.pontos ?? 0,
        entrouEm: toIso(data.entrouEm),
        status: data.status ?? "ativo",
      };
    });
  }

  async updateParticipantRole(leagueId: string, uid: string, role: LeagueRole): Promise<void> {
    // Papel é denormalizado em DOIS lugares (ver comentário em
    // FirestoreLeagueRepository) — precisa atualizar os dois juntos, senão
    // o botão de Configurações some/aparece errado pro usuário afetado.
    await Promise.all([
      updateDoc(doc(db, "groups", leagueId, "participants", uid), { role }),
      updateDoc(this.groupRef(leagueId), { [`memberRoles.${uid}`]: role }),
    ]);
    await this.pushHistory(leagueId, {
      type: "rule_updated",
      descricao: `Papel de participante atualizado para ${role}.`,
      administrador: auth.currentUser?.displayName?.trim() || "Você",
    });
  }

  async removeParticipant(leagueId: string, uid: string): Promise<void> {
    const groupSnap = await getDoc(this.groupRef(leagueId));
    const memberUids = ((groupSnap.data()?.memberUids as string[] | undefined) ?? []).filter((id) => id !== uid);
    const memberRoles = { ...(groupSnap.data()?.memberRoles as Record<string, LeagueRole> | undefined) };
    delete memberRoles[uid];

    await Promise.all([
      deleteDoc(doc(db, "groups", leagueId, "participants", uid)),
      updateDoc(this.groupRef(leagueId), {
        memberUids,
        memberRoles,
        participantsCount: increment(-1),
      }),
    ]);
    await this.pushHistory(leagueId, {
      type: "rule_updated",
      descricao: "Um participante foi removido da liga.",
      administrador: auth.currentUser?.displayName?.trim() || "Você",
    });
  }

  // Sistema antigo de Perguntas Bônus livres (por rodada, texto aberto) —
  // substituído pelas 4 perguntas fixas de desfecho de campeonato
  // (BonusQuestionsRepository). Mantido aqui só pra satisfazer a interface;
  // nada na UI atual chama isto.
  async getBonusQuestions(_leagueId: string): Promise<BonusQuestion[]> {
    return [];
  }

  async createBonusQuestion(): Promise<void> {
    throw new Error("Perguntas bônus livres foram substituídas pelas perguntas fixas de desfecho do campeonato.");
  }
}
